import type {
  BusinessCenterAutomationActionRecord,
  BusinessCenterAutomationCategory,
  BusinessCenterAutomationConditionRecord,
  BusinessCenterAutomationDashboard,
  BusinessCenterAutomationLogRecord,
  BusinessCenterAutomationRunRecord,
  BusinessCenterAutomationStatus,
  BusinessCenterAutomationTriggerRecord,
  BusinessCenterAutomationTriggerType,
  BusinessCenterAutomationWorkflowRecord,
  BusinessCenterCompanyRecord,
  ClientPortalWorkspaceKey,
} from "../../lib/types";
import { loadBusinessCenterCompanies } from "./businessCenterCompanyUsersData";
import { createBusinessCenterOperationalStateStore } from "../../lib/businessCenterOperationalStateStore";

export const automationCategoryOptions: Array<{ value: BusinessCenterAutomationCategory; label: string; description: string }> = [
  { value: "email", label: "Email Automations", description: "Welcome, follow-up, proposal, agreement, and payment emails." },
  { value: "task", label: "Task Automations", description: "Create tasks, reminders, and escalation work items." },
  { value: "client", label: "Client Automations", description: "Client creation, onboarding, and portal activation." },
  { value: "project", label: "Project Automations", description: "Project creation, milestone, and completion workflows." },
  { value: "ar_recovery", label: "AR Recovery Automations", description: "Aging, escalations, and collector follow-up workflows." },
  { value: "kreatethedate", label: "Kreatethedate Automations", description: "RSVP, memory, archive, and media vault workflows." },
];

export const automationStatusOptions: BusinessCenterAutomationStatus[] = ["Active", "Draft", "Paused", "Failed", "Archived"];

export const automationTriggerOptions: BusinessCenterAutomationTriggerType[] = [
  "Record Created",
  "Record Updated",
  "Status Changed",
  "Date Reached",
  "Payment Received",
  "Agreement Signed",
];

export const automationWorkspaceOptions: Array<{ value: ClientPortalWorkspaceKey | "all"; label: string; description: string }> = [
  { value: "all", label: "All Workspaces", description: "Shared automation available to every tenant." },
  { value: "kreatethedate", label: "Kreatethedate", description: "RSVP, memory, archive, and media workflows." },
  { value: "truevance_connect", label: "Truevance Connect", description: "Consulting, project, and reporting workflows." },
  { value: "ar_recovery", label: "AR Recovery", description: "Recovery, aging, and collector workflows." },
];

export const automationConditionFieldOptions = [
  { value: "company", label: "Company" },
  { value: "workspace", label: "Workspace" },
  { value: "projectType", label: "Project Type" },
  { value: "clientType", label: "Client Type" },
  { value: "status", label: "Status" },
  { value: "servicePackage", label: "Service Package" },
];

export const automationConditionOperatorOptions = ["is", "is not", "contains", "equals", "before", "after"] as const;

export const automationActionOptions = [
  { value: "Send Email", label: "Send Email" },
  { value: "Create Task", label: "Create Task" },
  { value: "Assign User", label: "Assign User" },
  { value: "Update Status", label: "Update Status" },
  { value: "Generate Notification", label: "Generate Notification" },
  { value: "Create Portal Record", label: "Create Portal Record" },
  { value: "Create Project", label: "Create Project" },
  { value: "Create Lead", label: "Create Lead" },
] as const;

export interface AutomationFormInput {
  companyId: string;
  workspaceKey: ClientPortalWorkspaceKey | "all";
  category: BusinessCenterAutomationCategory;
  name: string;
  description: string;
  triggerType: BusinessCenterAutomationTriggerType;
  triggerEntityType: string;
  triggerEntityLabel: string;
  triggerFieldName: string;
  triggerSchedule: string;
  conditionField: string;
  conditionOperator: (typeof automationConditionOperatorOptions)[number];
  conditionValue: string;
  actionType: BusinessCenterAutomationActionRecord["type"];
  actionLabel: string;
  actionTarget: string;
}

const automationStore = createBusinessCenterOperationalStateStore<BusinessCenterAutomationWorkflowRecord[]>({
  moduleKey: "business_center_automation_workflows",
  scope: { scopeKey: "automation-engine", scopeType: "platform" },
  seed: createSeedAutomationWorkflows,
  normalize: (workflows) => workflows.map(normalizeWorkflow),
  isEmpty: (workflows) => !Array.isArray(workflows) || workflows.length === 0,
});

export function loadBusinessCenterAutomationWorkflows(): BusinessCenterAutomationWorkflowRecord[] {
  return automationStore.read();
}

export async function refreshBusinessCenterAutomationWorkflows() {
  return automationStore.refresh();
}

export function saveBusinessCenterAutomationWorkflows(workflows: BusinessCenterAutomationWorkflowRecord[]) {
  automationStore.write(workflows);
}

export function upsertBusinessCenterAutomationWorkflow(nextWorkflow: BusinessCenterAutomationWorkflowRecord) {
  const current = loadBusinessCenterAutomationWorkflows();
  const normalized = normalizeWorkflow({ ...nextWorkflow, updatedAt: new Date().toISOString() });
  const exists = current.some((workflow) => workflow.id === normalized.id);
  const workflows = exists ? current.map((workflow) => (workflow.id === normalized.id ? normalized : workflow)) : [normalized, ...current];
  saveBusinessCenterAutomationWorkflows(workflows);
  return workflows;
}

export function updateBusinessCenterAutomationWorkflow(
  workflowId: string,
  changes: Partial<BusinessCenterAutomationWorkflowRecord>,
) {
  const current = loadBusinessCenterAutomationWorkflows();
  const workflows = current.map((workflow) =>
    workflow.id === workflowId
      ? normalizeWorkflow({
          ...workflow,
          ...changes,
          updatedAt: new Date().toISOString(),
        })
      : workflow,
  );
  saveBusinessCenterAutomationWorkflows(workflows);
  return workflows;
}

export function createBusinessCenterAutomationWorkflow(input: AutomationFormInput): BusinessCenterAutomationWorkflowRecord {
  const company = resolveAutomationCompany(input.companyId);
  const now = new Date().toISOString();
  const workflowId = `automation-${slugify(company.companySlug)}-${slugify(input.name)}`;
  const workspaceKey = input.workspaceKey || companyToWorkspace(company);

  return normalizeWorkflow({
    id: workflowId,
    companyId: company.id,
    companyName: company.companyName,
    workspaceKey,
    category: input.category,
    name: input.name || "Untitled Automation",
    description: input.description || "Reusable automation workflow.",
    status: "Active",
    isActive: true,
    trigger: {
      id: `${workflowId}-trigger`,
      type: input.triggerType,
      entityType: input.triggerEntityType || "client",
      entityLabel: input.triggerEntityLabel || "Record",
      fieldName: input.triggerFieldName || null,
      schedule: input.triggerSchedule || null,
    },
    conditions: [
      {
        id: `${workflowId}-condition`,
        field: input.conditionField || "status",
        operator: input.conditionOperator,
        value: input.conditionValue || "active",
        label: `${input.conditionField || "status"} ${input.conditionOperator} ${input.conditionValue || "active"}`,
      },
    ],
    actions: [
      {
        id: `${workflowId}-action`,
        type: input.actionType,
        label: input.actionLabel || input.actionType,
        target: input.actionTarget || "Shared automation queue",
        config: {},
      },
    ],
    successCount: 0,
    failureCount: 0,
    lastRunAt: null,
    nextRunAt: input.triggerSchedule ? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() : null,
    createdAt: now,
    updatedAt: now,
    sourceModule: resolveSourceModule(input.category, workspaceKey),
    sourceWorkspace: workspaceKey,
  });
}

export function buildBusinessCenterAutomationDashboard(workflows: BusinessCenterAutomationWorkflowRecord[]): BusinessCenterAutomationDashboard {
  const recentRuns = collectRecentRuns(workflows);
  const activeWorkflows = workflows.filter((workflow) => workflow.isActive && workflow.status === "Active").length;
  const failedWorkflows = workflows.filter((workflow) => workflow.failureCount > workflow.successCount).length;
  const successTotals = workflows.reduce(
    (acc, workflow) => {
      acc.success += workflow.successCount;
      acc.failure += workflow.failureCount;
      return acc;
    },
    { success: 0, failure: 0 },
  );
  const attempts = successTotals.success + successTotals.failure;
  const categoryCounts = automationCategoryOptions.reduce(
    (acc, option) => ({ ...acc, [option.value]: workflows.filter((workflow) => workflow.category === option.value).length }),
    {} as Record<BusinessCenterAutomationCategory, number>,
  );
  const companyCounts = workflows.reduce<Record<string, number>>((acc, workflow) => {
    acc[workflow.companyName] = (acc[workflow.companyName] || 0) + 1;
    return acc;
  }, {});

  return {
    totalWorkflows: workflows.length,
    activeWorkflows,
    failedWorkflows,
    successRate: attempts === 0 ? 100 : Math.round((successTotals.success / attempts) * 100),
    recentRuns,
    upcomingScheduledActions: workflows
      .filter((workflow) => Boolean(workflow.nextRunAt))
      .sort((a, b) => (a.nextRunAt || "").localeCompare(b.nextRunAt || ""))
      .slice(0, 6),
    companyCounts,
    categoryCounts,
  };
}

export function buildAutomationWorkflowSummary(workflow: BusinessCenterAutomationWorkflowRecord) {
  return {
    triggerLabel: `${workflow.trigger.type} • ${workflow.trigger.entityLabel}`,
    conditionSummary: workflow.conditions.map((condition) => condition.label).join(" | "),
    actionSummary: workflow.actions.map((action) => action.label).join(" | "),
    scheduleLabel: workflow.nextRunAt ? formatRelativeDate(workflow.nextRunAt) : "No schedule",
  };
}

function createSeedAutomationWorkflows(): BusinessCenterAutomationWorkflowRecord[] {
  const companies = loadBusinessCenterCompanies().filter((company) => company.companyType !== "parent");
  const now = Date.now();
  const byBrand: Record<string, Array<{
    category: BusinessCenterAutomationCategory;
    name: string;
    description: string;
    trigger: BusinessCenterAutomationTriggerType;
    entityType: string;
    entityLabel: string;
    fieldName?: string;
    schedule?: string;
    conditionField: string;
    conditionOperator: (typeof automationConditionOperatorOptions)[number];
    conditionValue: string;
    actionType: BusinessCenterAutomationActionRecord["type"];
    actionLabel: string;
    actionTarget: string;
    sourceModule: string;
  }>> = {
    kreatethedate: [
      {
        category: "email",
        name: "Welcome Email",
        description: "Send a warm brand-intro email as soon as a new client is created.",
        trigger: "Record Created",
        entityType: "client",
        entityLabel: "Client Record",
        conditionField: "clientStatus",
        conditionOperator: "is",
        conditionValue: "active",
        actionType: "Send Email",
        actionLabel: "Send welcome email",
        actionTarget: "Kreatethedate welcome template",
        sourceModule: "Onboarding",
      },
      {
        category: "kreatethedate",
        name: "RSVP Hub Created",
        description: "Create a follow-up task when an RSVP hub launches.",
        trigger: "Status Changed",
        entityType: "portal_record",
        entityLabel: "RSVP Hub",
        conditionField: "workspace",
        conditionOperator: "is",
        conditionValue: "kreatethedate",
        actionType: "Create Task",
        actionLabel: "Create RSVP follow-up task",
        actionTarget: "Guest experience queue",
        sourceModule: "Portal",
      },
      {
        category: "kreatethedate",
        name: "Memory Hub Created",
        description: "Notify the team when the Memory Hub is ready.",
        trigger: "Record Created",
        entityType: "portal_record",
        entityLabel: "Memory Hub",
        conditionField: "workspace",
        conditionOperator: "is",
        conditionValue: "kreatethedate",
        actionType: "Generate Notification",
        actionLabel: "Notify memory team",
        actionTarget: "Memory hub channel",
        sourceModule: "Portal",
      },
    ],
    truevance_connect: [
      {
        category: "email",
        name: "Discovery Call Follow-Up",
        description: "Send discovery follow-up email after a new lead is logged.",
        trigger: "Record Created",
        entityType: "lead",
        entityLabel: "Lead Record",
        conditionField: "status",
        conditionOperator: "is",
        conditionValue: "new",
        actionType: "Send Email",
        actionLabel: "Send discovery follow-up",
        actionTarget: "Discovery follow-up template",
        sourceModule: "Sales",
      },
      {
        category: "project",
        name: "Project Started",
        description: "Create kickoff tasks when a project moves into active delivery.",
        trigger: "Status Changed",
        entityType: "project",
        entityLabel: "Project Record",
        conditionField: "projectStatus",
        conditionOperator: "is",
        conditionValue: "Active",
        actionType: "Create Task",
        actionLabel: "Create kickoff task set",
        actionTarget: "Project delivery queue",
        sourceModule: "Projects",
      },
      {
        category: "client",
        name: "Client Portal Activated",
        description: "Send a portal activation notification once the client access flag changes.",
        trigger: "Status Changed",
        entityType: "client",
        entityLabel: "Client Portal",
        conditionField: "portalAccessStatus",
        conditionOperator: "is",
        conditionValue: "active",
        actionType: "Create Portal Record",
        actionLabel: "Generate portal welcome note",
        actionTarget: "Client portal feed",
        sourceModule: "Client Center",
      },
    ],
    ar_recovery: [
      {
        category: "ar_recovery",
        name: "Aging Uploaded",
        description: "Create a follow-up task as soon as an aging file is uploaded.",
        trigger: "Record Created",
        entityType: "billing_record",
        entityLabel: "Aging File",
        conditionField: "status",
        conditionOperator: "is",
        conditionValue: "active",
        actionType: "Create Task",
        actionLabel: "Queue aging review task",
        actionTarget: "Recovery team queue",
        sourceModule: "Aging Management",
      },
      {
        category: "ar_recovery",
        name: "Escalation Triggered",
        description: "Notify leadership when an account moves into escalation.",
        trigger: "Status Changed",
        entityType: "client",
        entityLabel: "Customer Account",
        conditionField: "status",
        conditionOperator: "is",
        conditionValue: "escalated",
        actionType: "Generate Notification",
        actionLabel: "Escalate to leadership",
        actionTarget: "Recovery command center",
        sourceModule: "Recovery Dashboard",
      },
      {
        category: "email",
        name: "Payment Received",
        description: "Send a confirmation when a payment posts to the account.",
        trigger: "Payment Received",
        entityType: "billing_record",
        entityLabel: "Payment Record",
        conditionField: "status",
        conditionOperator: "is",
        conditionValue: "paid",
        actionType: "Send Email",
        actionLabel: "Send receipt",
        actionTarget: "Payment confirmation template",
        sourceModule: "Billing",
      },
    ],
  };

  return companies.flatMap((company, index) => {
    const templates = byBrand[company.brand] || byBrand.truevance_connect;
    const workspaceKey = companyToWorkspace(company);
    return templates.map((template, templateIndex) => {
      const id = `workflow-${slugify(company.companySlug)}-${slugify(template.name)}`;
      const successCount = 6 + index + templateIndex;
      const failureCount = templateIndex % 2 === 0 ? 0 : 1;
      const lastRunAt = new Date(now - (templateIndex + 1) * 86400000).toISOString();
      const nextRunAt = templateIndex % 2 === 0 ? new Date(now + (templateIndex + 1) * 86400000).toISOString() : null;
      const workflow: BusinessCenterAutomationWorkflowRecord = {
        id,
        companyId: company.id,
        companyName: company.companyName,
        workspaceKey,
        category: template.category,
        name: template.name,
        description: template.description,
        status: templateIndex % 2 === 0 ? "Active" : "Draft",
        isActive: templateIndex % 2 === 0,
        trigger: {
          id: `${id}-trigger`,
          type: template.trigger,
          entityType: template.entityType,
          entityLabel: template.entityLabel,
          fieldName: template.fieldName || null,
          schedule: template.schedule || null,
        },
        conditions: [
          {
            id: `${id}-condition`,
            field: template.conditionField,
            operator: template.conditionOperator,
            value: template.conditionValue,
            label: `${template.conditionField} ${template.conditionOperator} ${template.conditionValue}`,
          },
        ],
        actions: [
          {
            id: `${id}-action`,
            type: template.actionType,
            label: template.actionLabel,
            target: template.actionTarget,
            config: { sourceModule: template.sourceModule },
          },
        ],
        successCount,
        failureCount,
        lastRunAt,
        nextRunAt,
        createdAt: lastRunAt,
        updatedAt: lastRunAt,
        sourceModule: template.sourceModule,
        sourceWorkspace: workspaceKey,
      };
      return normalizeWorkflow(workflow);
    });
  });
}

function normalizeWorkflow(workflow: BusinessCenterAutomationWorkflowRecord): BusinessCenterAutomationWorkflowRecord {
  return {
    ...workflow,
    companyId: workflow.companyId || "company-parent-business-center",
    companyName: workflow.companyName || "Business Center Company",
    workspaceKey: workflow.workspaceKey || companyToWorkspace(resolveAutomationCompany(workflow.companyId || "")),
    category: workflow.category,
    name: workflow.name || "Untitled Automation",
    description: workflow.description || "Reusable automation workflow.",
    status: automationStatusOptions.includes(workflow.status) ? workflow.status : "Draft",
    isActive: Boolean(workflow.isActive),
    trigger: normalizeTrigger(workflow.trigger),
    conditions: Array.isArray(workflow.conditions) ? workflow.conditions.map(normalizeCondition) : [],
    actions: Array.isArray(workflow.actions) ? workflow.actions.map(normalizeAction) : [],
    successCount: Math.max(0, Math.floor(workflow.successCount || 0)),
    failureCount: Math.max(0, Math.floor(workflow.failureCount || 0)),
    lastRunAt: workflow.lastRunAt || null,
    nextRunAt: workflow.nextRunAt || null,
    createdAt: workflow.createdAt || new Date().toISOString(),
    updatedAt: workflow.updatedAt || new Date().toISOString(),
    sourceModule: workflow.sourceModule || "Automation Engine",
    sourceWorkspace: workflow.sourceWorkspace || workflow.workspaceKey || "all",
  };
}

function normalizeTrigger(trigger: BusinessCenterAutomationTriggerRecord): BusinessCenterAutomationTriggerRecord {
  return {
    ...trigger,
    id: trigger.id || `trigger-${slugify(trigger.entityLabel || trigger.entityType)}`,
    type: automationTriggerOptions.includes(trigger.type) ? trigger.type : "Record Created",
    entityType: trigger.entityType || "client",
    entityLabel: trigger.entityLabel || "Record",
    fieldName: trigger.fieldName || null,
    schedule: trigger.schedule || null,
  };
}

function normalizeCondition(condition: BusinessCenterAutomationConditionRecord): BusinessCenterAutomationConditionRecord {
  return {
    ...condition,
    id: condition.id || `condition-${slugify(condition.field || "status")}`,
    field: condition.field || "status",
    operator: automationConditionOperatorOptions.includes(condition.operator) ? condition.operator : "is",
    value: condition.value || "active",
    label: condition.label || `${condition.field || "status"} ${condition.operator || "is"} ${condition.value || "active"}`,
  };
}

function normalizeAction(action: BusinessCenterAutomationActionRecord): BusinessCenterAutomationActionRecord {
  return {
    ...action,
    id: action.id || `action-${slugify(action.label || action.type)}`,
    type: action.type,
    label: action.label || action.type,
    target: action.target || "Shared automation queue",
    config: action.config || {},
  };
}

function collectRecentRuns(workflows: BusinessCenterAutomationWorkflowRecord[]): BusinessCenterAutomationRunRecord[] {
  return workflows
    .flatMap((workflow, index) => [
      {
        id: `${workflow.id}-run-a`,
        workflowId: workflow.id,
        companyId: workflow.companyId,
        workspaceKey: workflow.workspaceKey,
        status: (index % 3 === 0 ? "Succeeded" : index % 3 === 1 ? "Running" : "Pending") as BusinessCenterAutomationRunRecord["status"],
        startedAt: workflow.lastRunAt || workflow.createdAt,
        completedAt: index % 3 === 0 ? workflow.lastRunAt || workflow.createdAt : null,
        summary: `${workflow.name} executed across ${workflow.companyName}.`,
        triggeredBy: workflow.trigger.entityLabel,
      },
      {
        id: `${workflow.id}-run-b`,
        workflowId: workflow.id,
        companyId: workflow.companyId,
        workspaceKey: workflow.workspaceKey,
        status: (workflow.failureCount > 0 ? "Failed" : "Succeeded") as BusinessCenterAutomationRunRecord["status"],
        startedAt: workflow.lastRunAt || workflow.createdAt,
        completedAt: workflow.lastRunAt || workflow.createdAt,
        summary: `${workflow.name} ${workflow.failureCount > 0 ? "needs review" : "completed"} for ${workflow.companyName}.`,
        triggeredBy: workflow.trigger.entityLabel,
      },
    ])
    .slice(0, 10);
}

function resolveAutomationCompany(companyId: string): BusinessCenterCompanyRecord {
  const companies = loadBusinessCenterCompanies();
  const existing = companies.find((company) => company.id === companyId);
  if (existing) return existing;
  return companies.find((company) => company.companyType !== "parent") || companies[0] || createFallbackCompany();
}

function createFallbackCompany(): BusinessCenterCompanyRecord {
  const now = new Date().toISOString();
  return {
    id: "company-business-center-fallback",
    companyName: "Business Center Company",
    companySlug: "business-center",
    companyType: "client_company",
    brand: "truevance_connect",
    status: "active",
    createdAt: now,
    updatedAt: now,
  };
}

function companyToWorkspace(company: BusinessCenterCompanyRecord): ClientPortalWorkspaceKey | "all" {
  if (company.brand === "kreatethedate") return "kreatethedate";
  if (company.brand === "ar_recovery") return "ar_recovery";
  return "truevance_connect";
}

function resolveSourceModule(category: BusinessCenterAutomationCategory, workspaceKey: ClientPortalWorkspaceKey | "all") {
  if (category === "ar_recovery") return "AR Recovery";
  if (category === "kreatethedate") return "Kreatethedate";
  if (workspaceKey === "kreatethedate") return "Kreatethedate";
  if (workspaceKey === "ar_recovery") return "AR Recovery";
  if (category === "project") return "Projects";
  if (category === "client") return "Client Center";
  if (category === "task") return "Tasks Center";
  return "Automation Engine";
}

function formatRelativeDate(isoDate: string) {
  const date = new Date(isoDate);
  if (!Number.isFinite(date.getTime())) return "No schedule";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function slugify(value: string) {
  return (
    value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .replace(/-{2,}/g, "-") || "automation"
  );
}
